import React, { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Scaffold } from 'layouts';
import {
  ConfirmationDialog,
  ConfirmationDialogRef,
  ListingPageHeader,
  SettingsMenu,
} from 'components/Shared';
import { TemplatesList } from 'components/Pages/ChecklistTemplates';
import { useChecklistTemplates } from 'hooks/api/useChecklistTemplates';
import { useToast } from 'components/ui/use-toast';
import type { ChecklistTemplate } from 'contracts/ChecklistTemplate';
import * as S from './styles';

export const ChecklistTemplatesListPage: React.FC = () => {
  const dialogRef = useRef<ConfirmationDialogRef>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const {
    getChecklistTemplates,
    deleteChecklistTemplate,
  } = useChecklistTemplates();

  const { data, isLoading } = useQuery({
    queryKey: ['checklistTemplates'],
    queryFn: getChecklistTemplates,
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao carregar modelos de checklist',
        duration: 5000,
      });
    },
  });

  const deleteMutation = useMutation(deleteChecklistTemplate, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('checklistTemplates');
      toast({
        title: 'Sucesso',
        description: 'Checklist excluído com sucesso',
        duration: 5000,
      });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao excluir checklist',
        duration: 5000,
      });
    },
  });

  const onDelete = useCallback(
    async (template: ChecklistTemplate): Promise<void> => {
      const confirmed = await dialogRef.current?.openDialog({
        title: 'Excluir checklist?',
        message: `Deseja excluir o checklist "${template.name}"?`,
      });

      if (confirmed) {
        deleteMutation.mutate(template.id);
      }
    },
    [deleteMutation]
  );

  return (
    <Scaffold>
      <SettingsMenu />
      <ConfirmationDialog ref={dialogRef} />
      <S.MainPanel>
        <ListingPageHeader
          icon={<S.BookContentIcon />}
          title="Modelos de Checklist"
          isLoading={isLoading}
          actions={
            <S.LinkButton
              to="/configuracoes/checklist/modelos/criar"
              size="small"
            >
              <S.PlusIcon /> Novo checklist
            </S.LinkButton>
          }
        />
        <TemplatesList
          templates={data?.checklists || []}
          onDelete={onDelete}
          deletingId={deleteMutation.isLoading ? deleteMutation.variables as number : null}
        />
      </S.MainPanel>
    </Scaffold>
  );
};
